// "When did this program last run?" for the CVE detail of an endpoint device. The installed-programs script
// (Get-InstalledPrograms) adds lastRunDate, daysSinceRun and runEvidence to every program, and
// cve.unusedWithCves lists the programs that have CVEs and have not run for a long time. This module turns that
// into the labels and the "remove or update" decision shown next to each program. Plain functions only.

const asList = (v) => (Array.isArray(v) ? v : []);

export const programKey = (name, version) => `${name}|${version}`;

// Where the date came from, for the tooltip.
const SOURCES = {
  running: 'a process or service of the program is running now',
  prefetch: 'Windows Prefetch (last launch of the program)',
  userassist: 'UserAssist (started from Start, the taskbar or Explorer)',
  bam: 'Background Activity Moderator (last execution)',
};

// Results from before the script reported usage have no runEvidence at all: nothing about it is shown then.
export const hasUsageData = (data) => asList(data?.programs).some(p => p && typeof p.runEvidence === 'string');

// name|version -> the program's usage fields.
export const usageIndex = (data) => {
  const index = new Map();
  asList(data?.programs).forEach((p) => {
    if (p && typeof p.runEvidence === 'string') index.set(programKey(p.name, p.version), p);
  });
  return index;
};

// The programs that have CVEs and are not in use, worst first.
export const unusedPrograms = (data) => asList(data?.cve?.unusedWithCves)
  .filter(Boolean)
  .slice()
  .sort((a, b) => (Number(b.highestScore) || 0) - (Number(a.highestScore) || 0));

export const daysAgoText = (days) => {
  if (days === null || typeof days === 'undefined' || days === '') return '';     // Number(null) is 0: not "Today"
  const d = Number(days);
  if (!Number.isFinite(d) || d < 0) return '';
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d < 60) return `${d} days ago`;
  if (d < 730) return `${Math.round(d / 30.4)} months ago`;
  return `${Math.round(d / 365.25)} years ago`;
};

// How a program's use reads in the table, and what to do about its CVEs.
//   decision: 'unused' (remove or update), 'in-use' (update), 'component' (a runtime or library: update),
//             'unknown' (could not be judged: no decision)
export const usageOf = (program, index, unusedKeys) => {
  const key = programKey(program?.name, program?.version);
  const u = index.get(key);
  const evidence = u?.runEvidence;
  const days = u && u.daysSinceRun !== null && typeof u.daysSinceRun !== 'undefined' ? Number(u.daysSinceRun) : null;
  const source = SOURCES[evidence] || '';
  const dateText = u?.lastRunDate ? `last run ${u.lastRunDate}` : '';
  const isUnused = unusedKeys.has(key);

  if (evidence === 'component') {
    return { decision: 'component', label: 'Runtime or library', detail: 'Not started as a program: update it', source };
  }
  if (isUnused || evidence === 'not-seen') {
    return {
      decision: 'unused',
      label: days !== null ? daysAgoText(days) : 'Not seen recently',
      detail: dateText || 'No launch on record',
      source,
    };
  }
  if (evidence === 'running') return { decision: 'in-use', label: 'Running now', detail: dateText, source };
  if (days !== null) return { decision: 'in-use', label: daysAgoText(days), detail: dateText, source };
  return { decision: 'unknown', label: 'Unknown', detail: 'No way to tell on this machine', source };
};

export const unusedKeySet = (data) => new Set(unusedPrograms(data).map(p => programKey(p.name, p.version)));
